import { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, ArrowLeft, User, Menu as MenuIcon, Wallet, Home as HomeIcon, Lamp } from 'lucide-react';
import axios from 'axios';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShopLayout({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user) {
      fetchCartCount();
    }
  }, [user, location.pathname]);

  const fetchCartCount = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/shop/cart/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCartCount(response.data.count);
    } catch (error) {
      // Not logged in or error
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const isHomePage = location.pathname === '/shop';
  const isCheckoutPage = location.pathname.includes('checkout');

  const CHIPS = [
    { id: 'all', label: 'For You', href: '/shop' },
    { id: 'cat_pooja_samagri', label: 'Pooja', href: '/shop/category/cat_pooja_samagri' },
    { id: 'cat_idols_statues', label: 'Idols', href: '/shop/category/cat_idols_statues' },
    { id: 'cat_mala_rudraksha', label: 'Mala', href: '/shop/category/cat_mala_rudraksha' },
    { id: 'cat_spiritual_books', label: 'Books', href: '/shop/category/cat_spiritual_books' },
    { id: 'cat_sw_puja_kits', label: 'Kits', href: '/shop/category/cat_sw_puja_kits' },
    { id: 'cat_home_decor', label: 'Decor', href: '/shop/category/cat_home_decor' },
    { id: 'cat_swadeshi', label: 'Swadeshi 🇮🇳', href: '/shop/category/cat_swadeshi' },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f1f3f6' }}>
      {/* WHITE STICKY HEADER per spec */}
      <header className="shop-fk-header" data-testid="shop-fk-header">
        <div className="shop-fk-header-top">
          {!isHomePage ? (
            <button
              onClick={() => navigate(-1)}
              className="shop-fk-icon-btn"
              data-testid="shop-back-btn"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <Link to="/" className="shop-fk-icon-btn" data-testid="back-to-app-btn" aria-label="Open menu">
              <MenuIcon className="w-5 h-5" />
            </Link>
          )}

          <form onSubmit={handleSearch} className="shop-fk-search">
            <Search className="shop-fk-search-icon w-4 h-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for puja items, idols..."
              className="shop-fk-search-input"
              data-testid="shop-search-input"
            />
          </form>

          <Link to="/shop/cart" className="shop-fk-icon-btn" data-testid="cart-btn" aria-label="Cart">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="badge-dot">{cartCount > 9 ? '9+' : cartCount}</span>
            )}
          </Link>

          {user ? (
            <Link to="/profile" className="shop-fk-icon-btn" data-testid="user-profile-btn" aria-label="Account">
              {user.avatar && !user.avatar.includes('ui-avatars.com') ? (
                <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5" />
              )}
            </Link>
          ) : (
            <Link
              to="/auth"
              className="text-xs font-bold text-[#FF7A00] px-2"
              data-testid="login-btn"
            >
              Login
            </Link>
          )}
        </div>

        <div className="shop-fk-tabs" data-testid="shop-fk-tabs">
          {CHIPS.map((c) => {
            const active = (c.id === 'all' && isHomePage) || location.pathname.includes(c.id);
            return (
              <Link
                key={c.id}
                to={c.href}
                className={`shop-fk-tab ${active ? 'is-active' : ''}`}
                data-testid={`shop-fk-tab-${c.id}`}
              >
                {c.label}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet context={{ user, cartCount, refreshCart: fetchCartCount }} />
      </main>

      {/* Mobile Bottom Nav — production spec: Home / You / Wallet / Cart / Menu */}
      {!isCheckoutPage && (
        <nav className="shop-bottom-nav" data-testid="shop-bottom-nav">
          <NavLink
            to="/shop"
            end
            className={({ isActive }) => `shop-bottom-nav-item ${isActive ? 'is-active' : ''}`}
            data-testid="bn-home"
          >
            <HomeIcon className="w-5 h-5" />
            <span>Home</span>
          </NavLink>

          <NavLink
            to="/shop/account"
            className={({ isActive }) => `shop-bottom-nav-item ${isActive ? 'is-active' : ''}`}
            data-testid="bn-you"
          >
            <User className="w-5 h-5" />
            <span>You</span>
          </NavLink>

          <NavLink
            to="/shop/wallet"
            className={({ isActive }) => `shop-bottom-nav-item ${isActive ? 'is-active' : ''}`}
            data-testid="bn-wallet"
          >
            <span style={{ position: 'relative' }}>
              <Wallet className="w-5 h-5" />
              <span className="bn-dot" aria-hidden="true" />
            </span>
            <span>Wallet</span>
          </NavLink>

          <NavLink
            to="/shop/cart"
            className={({ isActive }) => `shop-bottom-nav-item ${isActive ? 'is-active' : ''}`}
            data-testid="bn-cart"
          >
            <span style={{ position: 'relative' }}>
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="bn-badge" data-testid="bn-cart-badge">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </span>
            <span>Cart</span>
          </NavLink>

          <NavLink
            to="/shop/menu"
            className={({ isActive }) => `shop-bottom-nav-item ${isActive ? 'is-active' : ''}`}
            data-testid="bn-menu"
          >
            <MenuIcon className="w-5 h-5" />
            <span>Menu</span>
          </NavLink>
        </nav>
      )}

      {/* Footer - Only show on non-checkout pages */}
      {!isCheckoutPage && (
        <>
          <footer className="mt-8 mb-20 bg-white border-t border-gray-100">
            {/* Categories Grid */}
            <section className="shop-container py-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Categories</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3 mb-2">
                <Link to="/shop/category/cat_pooja_samagri" className="text-sm text-gray-600 hover:text-amber-600">Pooja Samagri</Link>
                <Link to="/shop/category/cat_mala_rudraksha" className="text-sm text-gray-600 hover:text-amber-600">Mala & Rudraksha</Link>
                <Link to="/shop/category/cat_diyas_candles" className="text-sm text-gray-600 hover:text-amber-600">Diyas & Essentials</Link>
                <Link to="/shop/category/cat_spiritual_books" className="text-sm text-gray-600 hover:text-amber-600">Spiritual Books</Link>
                <Link to="/shop/category/cat_idols_statues" className="text-sm text-gray-600 hover:text-amber-600">Temple Items</Link>
                <Link to="/shop/category/cat_clothing" className="text-sm text-gray-600 hover:text-amber-600">Devotional Clothes</Link>
                <Link to="/shop/category/cat_gifts" className="text-sm text-gray-600 hover:text-amber-600">Festive Kits</Link>
                <Link to="/shop/category/all" className="text-sm text-amber-600 font-medium hover:text-amber-700">View All</Link>
              </div>
            </section>

            {/* Quick Links - with background separation */}
            <section className="bg-gray-50 py-6">
              <div className="shop-container">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-8">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Shop</h3>
                    <div className="space-y-2">
                      <Link to="/shop/category/all" className="block text-sm text-gray-600 hover:text-amber-600">All Products</Link>
                      <Link to="/shop" className="block text-sm text-gray-600 hover:text-amber-600">Featured</Link>
                      <Link to="/shop/category/cat_gifts" className="block text-sm text-gray-600 hover:text-amber-600">Gifts</Link>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Help</h3>
                    <div className="space-y-2">
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Track Order</a>
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Shipping Info</a>
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Returns</a>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Company</h3>
                    <div className="space-y-2">
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">About Us</a>
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Contact</a>
                      <Link to="/" className="block text-sm text-gray-600 hover:text-amber-600">DharmBook</Link>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Legal</h3>
                    <div className="space-y-2">
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Privacy Policy</a>
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Terms of Use</a>
                      <a href="#" className="block text-sm text-gray-600 hover:text-amber-600">Refund Policy</a>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Bottom Section */}
            <section className="shop-container py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Logo */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <Lamp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="font-bold text-amber-600 text-lg">DharmShop</span>
                    <p className="text-xs text-gray-500">Part of DharmBook</p>
                  </div>
                </div>
                
                {/* Copyright */}
                <div className="text-sm text-gray-500 text-center">
                  <p>© 2026 DharmShop. All rights reserved.</p>
                </div>

                {/* Back to DharmBook */}
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 text-amber-600 text-sm font-medium hover:text-amber-700"
                >
                  <HomeIcon className="w-4 h-4" />
                  Back to DharmBook
                </Link>
              </div>
            </section>
          </footer>

          {/* Legacy bottom nav removed — now using `.shop-bottom-nav` defined above */}
        </>
      )}
    </div>
  );
}
